'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { ArrowRightCircleIcon, ArrowPathIcon, ArrowUpCircleIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { getCompanyLogoSrc } from '../utils/mediaUrl';

const API_BASE = 'https://api.insightos.co.uk';
const DATA_LAYER_API_BASE = (process.env.REACT_APP_MONGO_URL || 'https://analytics.insightos.co.uk').replace(/\/+$/, '');

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold text-gray-300">{label}</div>
      {children}
    </div>
  );
}

function toDateInputValue(value) {
  if (!value) return '';
  const s = value.toString();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function normalizeWebsite(value) {
  if (!value) return '';
  const raw = value.toString().trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

const emptyDraft = {
  ContactName: '',
  ClientName: '',
  ClientAddress: '',
  Telephone: '',
  Email: '',
  Notes: '',
  AssignedTo: '',
  Industry: '',
  Website: '',
  Source: '',
  LastContacted: '',
  NextFollowUp: '',
  IsActive: 1,
  Postcode: '',
};

export default function CustomersCRMPanel() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [dataTables, setDataTables] = useState([]);
  const [dataLayerRevision, setDataLayerRevision] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [query, setQuery] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [draft, setDraft] = useState({ ...emptyDraft });
  const [selected, setSelected] = useState(null);
  
  // Projects expansion state
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [customerProjects, setCustomerProjects] = useState({});
  
  // Invoice state
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceSent, setInvoiceSent] = useState(false);
  const [invoiceSending, setInvoiceSending] = useState(false);
 
  
  // Invoice state
  const [invoiceData, setInvoiceData] = useState({
    projectId: null,
    projectName: '',
    clientName: '',
    projectPrice: 0,
    vatRate: 20,
    customerVatNumber: '',
    isVatDeductible: false,
    paymentLink: '',
    laborHours: 0,
    companyData: null,
    materials: [],
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => {
      const hay = [
        c.ContactName,
        c.ClientName,
        c.Email,
        c.Telephone,
        c.ClientAddress,
        c.Industry,
        c.Website,
        c.Source,
        c.AssignedTo,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [customers, query]);

  async function fetchStaff() {
    try {
      const res = await fetch(`${API_BASE}/api/staff`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) return;

      const rows = Array.isArray(data?.data) ? data.data : [];
      const names = rows
        .map((row) => (row?.Name ?? row?.StaffName ?? row?.name ?? '').toString().trim())
        .filter(Boolean);

      const unique = Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
      setStaffOptions(unique);
    } catch {
      // non-fatal
    }
  }

  async function fetchCustomers() {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/customers`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to load customers');
      setCustomers(Array.isArray(data?.customers) ? data.customers : []);
    } catch (e) {
      setError(e?.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchCustomers();
    fetchStaff();
  }, []);

  useEffect(() => {
    const fetchDataLayerTables = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(`${DATA_LAYER_API_BASE}/api/data-layer/schema`, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (res.ok && data?.data?.active) {
          setDataTables(data.data.active.tables || []);
          setDataLayerRevision(data.data.active.revision ?? null);
        }
      } catch {
        // The CRM stays available when the optional Data Layer sidebar cannot load.
      }
    };
    fetchDataLayerTables();
  }, []);

  function openCreate() {
    setDraft({ ...emptyDraft });
    setCreateOpen(true);
  }

  function openEdit(customer) {
    setSelected(customer);
    setDraft({
      ContactName: customer?.ContactName ?? '',
      ClientName: customer?.ClientName ?? '',
      ClientAddress: customer?.ClientAddress ?? '',
      Telephone: customer?.Telephone ?? '',
      Email: customer?.Email ?? '',
      Notes: customer?.Notes ?? '',
      AssignedTo: customer?.AssignedTo ?? '',
      Industry: customer?.Industry ?? '',
      Website: customer?.Website ?? '',
      Source: customer?.Source ?? '',
      LastContacted: toDateInputValue(customer?.LastContacted),
      NextFollowUp: toDateInputValue(customer?.NextFollowUp),
      IsActive: customer?.IsActive ?? 1,
      Postcode: customer?.Postcode ?? '',
    });
    setEditOpen(true);
  }

  async function createCustomer() {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/customers`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to create customer');
      setCreateOpen(false);
      await fetchCustomers();
    } catch (e) {
      setError(e?.message || 'Failed to create customer');
    } finally {
      setSaving(false);
    }
  }

  async function updateCustomer() {
    if (!selected?.id) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/customers/${selected.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to update customer');
      setEditOpen(false);
      setSelected(null);
      await fetchCustomers();
    } catch (e) {
      setError(e?.message || 'Failed to update customer');
    } finally {
      setSaving(false);
    }
  }

  async function fetchCustomerProjects(customerId) {
    try {
      const res = await fetch(`${API_BASE}/api/getprojectscustomer/${customerId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch projects');
      const data = await res.json();
      if (data.success) {
        setCustomerProjects(prev => ({ ...prev, [customerId]: data.data }));
      }
    } catch (e) {
      console.error('Error fetching projects:', e);
    }
  }

  function toggleProjectsExpansion(customerId) {
    if (expandedCustomer === customerId) {
      setExpandedCustomer(null);
    } else {
      setExpandedCustomer(customerId);
      if (!customerProjects[customerId]) {
        fetchCustomerProjects(customerId);
      }
    }
  }

  async function toggleIsActive(customer) {
    if (!customer?.id) return;
    setSaving(true);
    setError('');

    const nextIsActive = Number(customer.IsActive) === 1 ? 0 : 1;
    const payload = {
      ContactName: customer?.ContactName ?? '',
      ClientName: customer?.ClientName ?? '',
      ClientAddress: customer?.ClientAddress ?? '',
      Telephone: customer?.Telephone ?? '',
      Email: customer?.Email ?? '',
      Notes: customer?.Notes ?? '',
      AssignedTo: customer?.AssignedTo ?? '',
      Industry: customer?.Industry ?? '',
      Website: customer?.Website ?? '',
      Source: customer?.Source ?? '',
      LastContacted: toDateInputValue(customer?.LastContacted),
      NextFollowUp: toDateInputValue(customer?.NextFollowUp),
      IsActive: nextIsActive,
    };

    try {
      const res = await fetch(`${API_BASE}/api/customers/${customer.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to update IsActive');
      await fetchCustomers();
    } catch (e) {
      setError(e?.message || 'Failed to update IsActive');
    } finally {
      setSaving(false);
    }
  }

  async function openInvoiceModal(project, customer) {
    setSaving(true);
    setInvoiceSending(false);
    try {
      // Fetch project steps to calculate labor hours
      const stepsResponse = await fetch(`${API_BASE}/api/getprojectsteps/${project.id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const stepsData = await stepsResponse.json();

      let totalHours = 0;
      if (stepsResponse.ok && stepsData.data) {
        // Get the total count of all project steps
        totalHours = stepsData.data.length;
      }

      // Fetch company data
      const companyResponse = await fetch(`${API_BASE}/api/company`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const companyData = await companyResponse.json();

      // Resolve logo URL from API response (Bunny proxy or legacy fallback)
      let companyDataWithLogo = null;
      if (companyResponse.ok && companyData.data && companyData.data[0]) {
        const company = companyData.data[0];
        companyDataWithLogo = { ...company, CompanyLogoSrc: getCompanyLogoSrc(company) };
      }

      // Parse project materials if they exist
      let projectMaterials = [];
      if (project.Materials) {
        try {
          const parsedMaterials = typeof project.Materials === 'string' 
            ? JSON.parse(project.Materials) 
            : project.Materials;
          // Convert project materials to invoice materials format (only used materials)
          projectMaterials = parsedMaterials
            .filter(m => m.used === true)
            .map(m => ({
              description: `${m.name}${m.category ? ` (${m.category})` : ''}`,
              price: m.unit_cost || 0
            }));
        } catch (e) {
          console.error('Error parsing project materials:', e);
        }
      }

      setInvoiceData({
        projectId: project.id,
        projectName: project.Name,
        clientName: customer?.ClientName || project.ClientName || '',
        projectPrice: parseFloat(project.Price || 0),
        vatRate: Number(project?.VAT) || 20,
        customerVatNumber: project?.VATNumber || '',
        isVatDeductible: false,
        paymentLink: project?.PaymentLink || '',
        laborHours: totalHours,
        companyData: companyDataWithLogo,
        materials: projectMaterials,
      });
      setInvoiceSent(Number(project?.InvoiceSent) === 1);
      setInvoiceOpen(true);
    } catch (err) {
      console.error('Error opening invoice modal:', err);
      setError('Failed to load invoice data');
    } finally {
      setSaving(false);
    }
  }

  async function sendInvoice() {
    if (!invoiceData.projectId) return;
    setInvoiceSending(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/sendInvoice`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: invoiceData.projectId,
          paymentLink: invoiceData.paymentLink,
          vatRate: invoiceData.vatRate,
          vatNumber: invoiceData.customerVatNumber,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to send invoice');
      setInvoiceSent(true);
    } catch (err) {
      console.error('Error sending invoice:', err);
      setError(err?.message || 'Failed to send invoice');
    } finally {
      setInvoiceSending(false);
    }
  }

  async function markProjectPaid(projectId, customerId) {
    if (!projectId) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/projectpaid/${projectId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to mark project as paid');
      if (customerId) {
        await fetchCustomerProjects(customerId);
      }
    } catch (err) {
      setError(err?.message || 'Failed to mark project as paid');
    } finally {
      setSaving(false);
    }
  }

  async function generateInvoice(action) {
    setSaving(true);
    try {
      // Don't send companyData in request body to avoid payload size issues
      const { companyData, ...invoicePayload } = invoiceData;
      const response = await fetch(`${API_BASE}/api/createinvoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...invoicePayload, action }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to generate invoice');
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to generate invoice');
      }
      
      setShowInvoiceModal(false);
      setSuccess(data.message || 'Invoice sent. Please check your emails.');
    } catch (err) {
      console.error('Error generating invoice:', err);
      setError(err?.message || 'Failed to generate invoice');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="crm-dark min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-black px-4 py-8 sm:px-6 lg:px-8" data-component="crm-customers" data-feature="sales-project-crm">
      <style>{`
        .crm-dark .bg-white, .crm-dark .bg-white\\/90, .crm-dark .bg-gray-50 { background-color: rgb(17 24 39) !important; }
        .crm-dark .bg-slate-900 { background-color: rgb(15 23 42) !important; }
        .crm-dark .bg-emerald-50, .crm-dark .bg-emerald-100 { background-color: rgb(6 78 59 / 0.32) !important; }
        .crm-dark .bg-red-50, .crm-dark .bg-red-100 { background-color: rgb(127 29 29 / 0.32) !important; }
        .crm-dark .bg-blue-50 { background-color: rgb(8 47 73 / 0.45) !important; }
        .crm-dark .border-gray-100, .crm-dark .border-gray-200 { border-color: rgb(55 65 81) !important; }
        .crm-dark .text-gray-900, .crm-dark .text-gray-800, .crm-dark .text-gray-700 { color: rgb(243 244 246) !important; }
        .crm-dark .text-gray-600, .crm-dark .text-gray-500 { color: rgb(156 163 175) !important; }
        .crm-dark input:not([type='range']), .crm-dark select, .crm-dark textarea { border-color: rgb(55 65 81) !important; background-color: rgb(3 7 18) !important; color: rgb(243 244 246) !important; }
        .crm-dark input:focus, .crm-dark select:focus, .crm-dark textarea:focus { border-color: rgb(34 211 238) !important; box-shadow: 0 0 0 1px rgb(34 211 238 / 0.45) !important; }
        .crm-dark input::placeholder, .crm-dark textarea::placeholder { color: rgb(107 114 128) !important; }
        .crm-dark option { background-color: rgb(3 7 18); color: rgb(243 244 246); }
      `}</style>
      <div className={`mx-auto grid max-w-[90rem] gap-5 ${sidebarCollapsed ? 'xl:grid-cols-[4.5rem_minmax(0,1fr)]' : 'xl:grid-cols-[17rem_minmax(0,1fr)]'}`}>
        <aside className="order-1 h-fit overflow-hidden rounded-xl border border-white/10 bg-gray-900/80 shadow-xl shadow-black/20 xl:sticky xl:top-6">
          <div className={`border-b border-white/10 py-4 ${sidebarCollapsed ? 'px-3 text-center' : 'px-5'}`}>
            <button type="button" onClick={() => setSidebarCollapsed((collapsed) => !collapsed)} className="mb-3 inline-flex size-8 items-center justify-center rounded-md bg-gray-800/60 text-gray-300 hover:bg-gray-700" aria-label={sidebarCollapsed ? 'Expand Data Layer sidebar' : 'Collapse Data Layer sidebar'}>
              {sidebarCollapsed ? <ChevronRightIcon className="size-5" /> : <ChevronLeftIcon className="size-5" />}
            </button>
            {!sidebarCollapsed && <><p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Data Layer</p><h2 className="mt-1 text-lg font-semibold text-white">Your tables</h2><p className="mt-1 text-xs text-gray-400">{dataLayerRevision !== null ? `Revision ${dataLayerRevision}` : 'Sales workspace'}</p></>}
          </div>
          <nav aria-label="Data Layer navigation" className="p-3">
            {dataTables.map((table) => <button key={table.tableKey} type="button" onClick={() => navigate('/data-layer')} className="group mb-1 flex w-full items-center gap-x-3 rounded-md px-3 py-3 text-left text-sm text-gray-300 transition hover:bg-gray-800/60 hover:text-white"><ArrowPathIcon className="size-5 shrink-0 text-gray-500 group-hover:text-white" />{!sidebarCollapsed && <><span className="min-w-0 flex-1 truncate font-semibold">{table.name}</span><span className="shrink-0 text-xs text-gray-500">{table.fields?.length || 0}</span></>}</button>)}
            <button type="button" className="group mt-3 flex w-full items-center gap-x-3 border-t border-white/10 bg-cyan-950/30 px-3 py-3 text-left text-sm text-cyan-100" aria-current="page"><SparklesIcon className="size-5 shrink-0 text-cyan-300" />{!sidebarCollapsed && <span className="min-w-0 flex-1 truncate font-semibold">Sales / Project CRM</span>}</button>
          </nav>
          {!sidebarCollapsed && <div className="border-t border-white/10 px-5 py-4"><p className="text-xs leading-5 text-gray-400">Customer relationships, delivery work, and invoicing.</p></div>}
        </aside>
        <main className="order-2 min-w-0">
      <div className="overflow-hidden rounded-xl border border-white/10 bg-gray-900/80 shadow-xl shadow-black/20">
      <div className="border-b border-cyan-800/60 bg-gradient-to-r from-cyan-950 via-blue-950 to-emerald-950 p-5 sm:p-7">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-100">Sales workspace</div>
            <div className="mt-1 text-2xl font-semibold text-white sm:text-3xl">Sales / Project CRM</div>
            <div className="mt-1 text-sm text-white/90">Client contacts, follow-ups, delivery work, and invoices in one place.</div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative w-full sm:w-80">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search client, contact, email..."
                className="w-full rounded-md border border-white/40 bg-white px-3 py-2 text-sm text-slate-900 shadow-lg focus:outline-none focus:ring-2 focus:ring-cyan-200"
              />
            </div>
            <button
              type="button"
              onClick={openCreate}
              className="rounded-md bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-slate-800"
              data-component="crm-customers"
              data-feature="add_customer"
            >
              Add Customer
            </button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2 text-xs font-semibold text-white">
          <div className="border border-white/30 bg-slate-950/25 px-3 py-1.5">Total clients: {customers.length}</div>
          <div className="border border-white/30 bg-slate-950/25 px-3 py-1.5">Visible: {filtered.length}</div>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {/* Mobile cards */}
      <div className="mt-4 grid grid-cols-1 gap-3 p-4 md:hidden">
        {loading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-gray-500">No customers found.</div>
        ) : (
          filtered.map((c) => {
            const isActive = Number(c.IsActive) === 1;
            const websiteHref = normalizeWebsite(c.Website);
            return (
              <div
                key={c.id}
                className={`rounded-lg border p-4 shadow-lg ${isActive ? 'border-emerald-400/40 bg-emerald-50' : 'border-red-400/40 bg-red-50'}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{c.ClientName || 'Unnamed Customer'}</div>
                    <div className="text-xs text-gray-500 truncate">{c.ContactName || '—'}</div>
                    <div className="mt-2 text-sm text-gray-700 break-words">{c.ClientAddress || '—'}</div>
                    <div className="mt-2 text-sm text-gray-700">
                      <div><span className="font-semibold">Tel:</span> {c.Telephone || '—'}</div>
                      <div className="truncate"><span className="font-semibold">Email:</span> {c.Email || '—'}</div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-semibold">Industry:</span> {c.Industry || '—'}
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-semibold">Source:</span> {c.Source || '—'}
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-semibold">Website:</span>{' '}
                      {websiteHref ? (
                        <a
                          className="underline"
                          href={websiteHref}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {c.Website}
                        </a>
                      ) : (
                        '—'
                      )}
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-semibold">AssignedTo:</span> {c.AssignedTo || '—'}
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-semibold">LastContacted:</span> {toDateInputValue(c.LastContacted) || '—'}
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-semibold">NextFollowUp:</span> {toDateInputValue(c.NextFollowUp) || '—'}
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-semibold">IsActive:</span> {Number(c.IsActive) === 1 ? '1' : '0'}
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-semibold">ProjectIds:</span> {c.ProjectIds || '—'}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => toggleIsActive(c)}
                      disabled={saving}
                      className={`rounded-2xl px-3 py-2 text-xs font-semibold text-white shadow-[0_8px_20px_rgba(15,23,42,0.18)] disabled:opacity-50 ${Number(c.IsActive) === 1 ? 'bg-red-600 hover:bg-red-500' : 'bg-emerald-600 hover:bg-emerald-500'}`}
                      data-component="crm-customers"
                      data-feature="toggle_active"
                    >
                      {Number(c.IsActive) === 1 ? 'Set Inactive (0)' : 'Set Active (1)'}
                    </button>
                    <button
                      type="button"
                      onClick={() => openEdit(c)}
                      className="rounded-2xl border border-white/45 bg-white/90 px-3 py-2 text-xs font-semibold text-gray-800 shadow-[0_8px_20px_rgba(15,23,42,0.12)] hover:bg-gray-50"
                      data-component="crm-customers"
                      data-feature="edit_customer"
                    >
                      Edit
                    </button>
                  </div>
                </div>

                {c.Notes ? (
                  <div className="mt-3 text-sm text-gray-600 whitespace-pre-wrap">{c.Notes}</div>
                ) : null}
              </div>
            );
          })
        )}
      </div>

      {/* Desktop table */}
      <div className="m-4 hidden overflow-hidden rounded-lg border border-slate-700 bg-white shadow-2xl md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900 text-cyan-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Client</th>
                <th className="px-4 py-3 text-left font-semibold">Contact</th>
                <th className="px-4 py-3 text-left font-semibold">Meta</th>
                <th className="px-4 py-3 text-left font-semibold">AssignedTo</th>
                <th className="px-4 py-3 text-left font-semibold">Follow-up</th>
                <th className="px-4 py-3 text-left font-semibold">Active</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td className="px-4 py-6 text-gray-500" colSpan={7}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td className="px-4 py-6 text-gray-500" colSpan={7}>No customers found.</td></tr>
              ) : (
                filtered.map((c) => (
                  <React.Fragment key={c.id}>
                    <tr
                      className={
                        Number(c.IsActive) === 1
                          ? 'bg-emerald-100 hover:bg-emerald-200'
                          : 'bg-red-100 hover:bg-red-200'
                      }
                    >
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{c.ClientName || 'Unnamed Customer'}</div>
                        <div className="text-xs text-gray-600">{c.ContactName || '—'}</div>
                        <div className="text-xs text-gray-500 line-clamp-1">{c.ClientAddress || '—'}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-800">
                        <div className="line-clamp-1">{c.Email || '—'}</div>
                        <div className="text-xs text-gray-600">{c.Telephone || '—'}</div>
                        <div className="mt-1">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${Number(c.IsActive) === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {Number(c.IsActive) === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-800">
                        <div className="text-xs"><span className="font-semibold">Industry:</span> {c.Industry || '—'}</div>
                        <div className="text-xs"><span className="font-semibold">Source:</span> {c.Source || '—'}</div>
                        <div className="text-xs">
                          <span className="font-semibold">Website:</span>{' '}
                          {c.Website ? (
                            <a className="underline" href={normalizeWebsite(c.Website)} target="_blank" rel="noreferrer">{c.Website}</a>
                          ) : (
                            '—'
                          )}
                        </div>
                        <div className="text-xs flex items-center gap-2">
                          <span className="font-semibold">Projects:</span>
                          <span>{c.ProjectIds || '—'}</span>
                          {c.ProjectIds && (
                            <button
                              type="button"
                              onClick={() => toggleProjectsExpansion(c.id)}
                              className="inline-flex items-center justify-center text-emerald-600 hover:text-emerald-700"
                            >
                              {expandedCustomer === c.id ? (
                                <ChevronUpIcon className="h-8 w-8" />
                              ) : (
                                <ChevronDownIcon className="h-8 w-8" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-800">{c.AssignedTo || '—'}</td>
                      <td className="px-4 py-3 text-gray-800">
                        <div className="text-xs"><span className="font-semibold">Last:</span> {toDateInputValue(c.LastContacted) || '—'}</div>
                        <div className="text-xs"><span className="font-semibold">Next:</span> {toDateInputValue(c.NextFollowUp) || '—'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => toggleIsActive(c)}
                          disabled={saving}
                          className={`rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50 ${Number(c.IsActive) === 1 ? 'bg-red-600 hover:bg-red-500' : 'bg-emerald-600 hover:bg-emerald-500'}`}
                        >
                          {Number(c.IsActive) === 1 ? 'Set 0' : 'Set 1'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            type="button"
                            onClick={() => openEdit(c)}
                            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50"
                          >
                            Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expanded projects section */}
                    {expandedCustomer === c.id && customerProjects[c.id] && (
                      <tr>
                        <td colSpan={7} className="px-4 py-4 bg-gray-50">
                          <div className="space-y-3">
                            <div className="font-semibold text-sm text-gray-900">Project Details:</div>
                            {customerProjects[c.id].length === 0 ? (
                              <p className="text-sm text-gray-500">No projects found.</p>
                            ) : (
                              customerProjects[c.id].map((project) => (
                                <div key={project.id} className={`rounded-lg border border-gray-200 p-4 shadow-sm ${Number(project.Paid) === 1 ? 'bg-emerald-100' : 'bg-white'}`}>
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                      <div>
                                        <span className="text-xs font-semibold text-gray-700">Name:</span>
                                        <p className="text-sm text-gray-900">{project.Name || '—'}</p>
                                      </div>
                                      <div>
                                        <span className="text-xs font-semibold text-gray-700">Type:</span>
                                        <p className="text-sm text-gray-900">{project.ProjectType || '—'}</p>
                                      </div>
                                      <div>
                                        <span className="text-xs font-semibold text-gray-700">Client:</span>
                                        <p className="text-sm text-gray-900">{project.ClientName || '—'}</p>
                                      </div>
                                      <div>
                                        <span className="text-xs font-semibold text-gray-700">Start Date:</span>
                                        <p className="text-sm text-gray-900">{toDateInputValue(project.StartDate) || '—'}</p>
                                      </div>
                                      <div>
                                        <span className="text-xs font-semibold text-gray-700">End Date:</span>
                                        <p className="text-sm text-gray-900">{toDateInputValue(project.EndDate) || '—'}</p>
                                      </div>
                                      <div>
                                        <span className="text-xs font-semibold text-gray-700">Author:</span>
                                        <p className="text-sm text-gray-900">{project.Author || '—'}</p>
                                      </div>
                                      <div>
                                        <span className="text-xs font-semibold text-gray-700">Staff:</span>
                                        <p className="text-sm text-gray-900">{project.Staff || '—'}</p>
                                      </div>
                                      <div>
                                        <span className="text-xs font-semibold text-gray-700">Process:</span>
                                        <p className="text-sm text-gray-900">{project.Process || '—'}</p>
                                      </div>
                                      <div>
                                        <span className="text-xs font-semibold text-gray-700">Status:</span>
                                        <p className="text-sm">
                                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${Number(project.Completed) === 1 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                            {Number(project.Completed) === 1 ? 'Completed' : 'In Progress'}
                                          </span>
                                        </p>
                                      </div>
                                      <div>
                                        <span className="text-xs font-semibold text-gray-700">Active:</span>
                                        <p className="text-sm">
                                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${Number(project.Active) === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                            {Number(project.Active) === 1 ? 'Active' : 'Inactive'}
                                          </span>
                                        </p>
                                      </div>
                                      <div>
                                        <span className="text-xs font-semibold text-gray-700">Created:</span>
                                        <p className="text-sm text-gray-900">{toDateInputValue(project.CreatedAt) || '—'}</p>
                                      </div>
                                      <div>
                                        <span className="text-xs font-semibold text-gray-700">Company Code:</span>
                                        <p className="text-sm text-gray-900">{project.CompanyCode || '—'}</p>
                                      </div>
                                      <div className="md:col-span-2">
                                        <span className="text-xs font-semibold text-gray-700">Description:</span>
                                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{project.Description || '—'}</p>
                                      </div>
                                      <div className="md:col-span-2">
                                        <span className="text-xs font-semibold text-gray-700">Objective:</span>
                                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{project.Objective || '—'}</p>
                                      </div>
                                      <div className="md:col-span-2">
                                        <span className="text-xs font-semibold text-gray-700">Report:</span>
                                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{project.Report || '—'}</p>
                                      </div>
                                    </div>
                                    <div className="ml-4 flex flex-col gap-2">
                                      <button
                                        type="button"
                                        onClick={() => navigate('/project-management', { state: { projectId: project.id } })}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-emerald-500 hover:text-emerald-600 hover:animate-bounce shadow-sm border border-emerald-200"
                                        title="Go to Project Management"
                                      >
                                        <ArrowRightCircleIcon className="h-5 w-5" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => openInvoiceModal(project, c)}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-blue-500 hover:text-blue-600 hover:animate-bounce shadow-sm border border-blue-200"
                                        title="Create Invoice"
                                      >
                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                        </svg>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => markProjectPaid(project.id, c.id)}
                                        disabled={saving || Number(project.Paid) === 1}
                                        className={`inline-flex h-8 w-8 items-center justify-center rounded-full shadow-sm border ${Number(project.Paid) === 1 ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-600 border-emerald-200 hover:text-emerald-700'}`}
                                        title={Number(project.Paid) === 1 ? 'Paid' : 'Mark as Paid'}
                                      >
                                        £
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create modal */}
      <Dialog open={createOpen} onClose={() => !saving && setCreateOpen(false)} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
          <div className="mx-auto max-w-2xl">
            <DialogPanel className="rounded-2xl bg-white p-4 sm:p-6 shadow-xl">
              <div className="flex items-center justify-between">
                <div className="text-lg font-semibold text-gray-900">Add Customer</div>
                <button
                  type="button"
                  onClick={() => setCreateOpen(false)}
                  disabled={saving}
                  className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                >
                  Close
                </button>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="IsActive (1/0)">
                  <button
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, IsActive: Number(d.IsActive) === 1 ? 0 : 1 }))}
                    className={`w-full rounded-lg px-3 py-2 text-sm font-semibold ${Number(draft.IsActive) === 1 ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}
                  >
                    {Number(draft.IsActive) === 1 ? 'Active (1)' : 'Inactive (0)'}
                  </button>
                </Field>

                <Field label="AssignedTo">
                  <select
                    value={draft.AssignedTo}
                    onChange={(e) => setDraft((d) => ({ ...d, AssignedTo: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  >
                    <option value="">Unassigned</option>
                    {staffOptions.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Contact Name">
                  <input
                    value={draft.ContactName}
                    onChange={(e) => setDraft((d) => ({ ...d, ContactName: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="Client Name">
                  <input
                    value={draft.ClientName}
                    onChange={(e) => setDraft((d) => ({ ...d, ClientName: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="Email">
                  <input
                    value={draft.Email}
                    onChange={(e) => setDraft((d) => ({ ...d, Email: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="Industry">
                  <input
                    value={draft.Industry}
                    onChange={(e) => setDraft((d) => ({ ...d, Industry: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="Website">
                  <input
                    value={draft.Website}
                    onChange={(e) => setDraft((d) => ({ ...d, Website: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="Source">
                  <input
                    value={draft.Source}
                    onChange={(e) => setDraft((d) => ({ ...d, Source: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="Last Contacted">
                  <input
                    type="date"
                    value={draft.LastContacted}
                    onChange={(e) => setDraft((d) => ({ ...d, LastContacted: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="Next Follow Up">
                  <input
                    type="date"
                    value={draft.NextFollowUp}
                    onChange={(e) => setDraft((d) => ({ ...d, NextFollowUp: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <div className="sm:col-span-2">
                  <Field label="Client Address">
                    <textarea
                      value={draft.ClientAddress}
                      onChange={(e) => setDraft((d) => ({ ...d, ClientAddress: e.target.value }))}
                      rows={3}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </Field>
                </div>

                <Field label="Postcode">
                  <input
                    value={draft.Postcode}
                    onChange={(e) => setDraft((d) => ({ ...d, Postcode: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="Telephone">
                  <input
                    value={draft.Telephone}
                    onChange={(e) => setDraft((d) => ({ ...d, Telephone: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <div className="sm:col-span-2">
                  <Field label="Notes">
                    <textarea
                      value={draft.Notes}
                      onChange={(e) => setDraft((d) => ({ ...d, Notes: e.target.value }))}
                      rows={4}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </Field>
                </div>
              </div>

              <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setCreateOpen(false)}
                  disabled={saving}
                  className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={createCustomer}
                  disabled={saving}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Create'}
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {/* Edit modal */}
      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
          <div className="mx-auto max-w-2xl">
            <DialogPanel className="rounded-2xl bg-white p-4 sm:p-6 shadow-xl">
              <div className="flex items-center justify-between">
                <div className="text-lg font-semibold text-gray-900">Edit Customer</div>
                <button
                  type="button"
                  onClick={() => setEditOpen(false)}
                  disabled={saving}
                  className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                >
                  Close
                </button>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="IsActive (1/0)">
                  <button
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, IsActive: Number(d.IsActive) === 1 ? 0 : 1 }))}
                    className={`w-full rounded-lg px-3 py-2 text-sm font-semibold ${Number(draft.IsActive) === 1 ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}
                  >
                    {Number(draft.IsActive) === 1 ? 'Active (1)' : 'Inactive (0)'}
                  </button>
                </Field>

                <Field label="AssignedTo">
                  <select
                    value={draft.AssignedTo}
                    onChange={(e) => setDraft((d) => ({ ...d, AssignedTo: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  >
                    <option value="">Unassigned</option>
                    {staffOptions.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Contact Name">
                  <input
                    value={draft.ContactName}
                    onChange={(e) => setDraft((d) => ({ ...d, ContactName: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="ClientName">
                  <input
                    value={draft.ClientName}
                    onChange={(e) => setDraft((d) => ({ ...d, ClientName: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="Email">
                  <input
                    value={draft.Email}
                    onChange={(e) => setDraft((d) => ({ ...d, Email: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="Industry">
                  <input
                    value={draft.Industry}
                    onChange={(e) => setDraft((d) => ({ ...d, Industry: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="Website">
                  <input
                    value={draft.Website}
                    onChange={(e) => setDraft((d) => ({ ...d, Website: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="Source">
                  <input
                    value={draft.Source}
                    onChange={(e) => setDraft((d) => ({ ...d, Source: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="Last Contacted">
                  <input
                    type="date"
                    value={draft.LastContacted}
                    onChange={(e) => setDraft((d) => ({ ...d, LastContacted: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="Next Follow Up">
                  <input
                    type="date"
                    value={draft.NextFollowUp}
                    onChange={(e) => setDraft((d) => ({ ...d, NextFollowUp: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <div className="sm:col-span-2">
                  <Field label="ClientAddress">
                    <textarea
                      value={draft.ClientAddress}
                      onChange={(e) => setDraft((d) => ({ ...d, ClientAddress: e.target.value }))}
                      rows={3}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </Field>
                </div>

                <Field label="Postcode">
                  <input
                    value={draft.Postcode}
                    onChange={(e) => setDraft((d) => ({ ...d, Postcode: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="Telephone">
                  <input
                    value={draft.Telephone}
                    onChange={(e) => setDraft((d) => ({ ...d, Telephone: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <div className="sm:col-span-2">
                  <Field label="Notes">
                    <textarea
                      value={draft.Notes}
                      onChange={(e) => setDraft((d) => ({ ...d, Notes: e.target.value }))}
                      rows={4}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </Field>
                </div>
              </div>

              <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setEditOpen(false)}
                    disabled={saving}
                    className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={updateCustomer}
                    disabled={saving}
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {/* Invoice modal */}
      <Dialog open={invoiceOpen} onClose={() => !saving && setInvoiceOpen(false)} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
          <div className="mx-auto max-w-4xl">
            <DialogPanel className="rounded-2xl bg-white p-4 sm:p-6 shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <div className="text-lg font-semibold text-gray-900">Create Invoice - {invoiceData.projectName}</div>
                <button
                  type="button"
                  onClick={() => setInvoiceOpen(false)}
                  disabled={saving}
                  className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                >
                  Close
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Field label="Project Price">
                  <input
                    type="number"
                    value={invoiceData.projectPrice}
                    onChange={(e) => setInvoiceData(d => ({ ...d, projectPrice: parseFloat(e.target.value) || 0 }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <Field label="Labor Hours">
                  <input
                    type="number"
                    value={invoiceData.laborHours.toFixed(2)}
                    readOnly
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-gray-50"
                  />
                </Field>

                <Field label={`VAT Rate (${invoiceData.vatRate}%)`}>
                  <input
                    type="range"
                    min="0"
                    max="25"
                    step="0.5"
                    value={invoiceData.vatRate}
                    onChange={(e) => setInvoiceData(d => ({ ...d, vatRate: parseFloat(e.target.value) }))}
                    className="w-full"
                  />
                  <div className="text-xs text-gray-600 mt-1">Current: {invoiceData.vatRate}%</div>
                </Field>

                <Field label="Customer VAT Number">
                  <input
                    value={invoiceData.customerVatNumber}
                    onChange={(e) => setInvoiceData(d => ({ ...d, customerVatNumber: e.target.value }))}
                    placeholder="GB123456789"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={invoiceData.isVatDeductible}
                    onChange={(e) => setInvoiceData(d => ({ ...d, isVatDeductible: e.target.checked }))}
                    className="rounded"
                  />
                  <label className="text-sm text-gray-700">VAT Deductible (minus 20%)</label>
                </div>

                <Field label="Payment Link">
                  <input
                    value={invoiceData.paymentLink}
                    onChange={(e) => setInvoiceData(d => ({ ...d, paymentLink: e.target.value }))}
                    placeholder="https://payment.link"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </Field>
              </div>

              {/* Materials Section - From Project */}
              <div className="mb-6">
                <div className="mb-3">
                  <label className="text-sm font-semibold text-gray-700">Materials (from project)</label>
                  <p className="text-xs text-gray-500 mt-1">Materials are automatically loaded from the project. Only used materials are included.</p>
                </div>
                <div className="space-y-2">
                  {invoiceData.materials.map((material, idx) => (
                    <div key={idx} className="flex gap-2 bg-gray-50 p-2 rounded-lg">
                      <div className="flex-1 text-sm text-gray-900">{material.description}</div>
                      <div className="text-sm font-semibold text-gray-900">£{material.price.toFixed(2)}</div>
                    </div>
                  ))}
                  {invoiceData.materials.length === 0 && (
                    <div className="text-sm text-gray-500 italic bg-gray-50 p-3 rounded-lg">No used materials in this project</div>
                  )}
                </div>
              </div>

              {/* Invoice Preview */}
              <div className="border border-gray-200 rounded-lg p-6 bg-gray-50 mb-6">
                <div className="text-lg font-semibold mb-4">Invoice Preview</div>
                {invoiceData.companyData && (
                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <div className="text-xl font-bold">{invoiceData.companyData.CompanyName}</div>
                        <div className="text-sm text-gray-600 mt-1">Company No: {invoiceData.companyData.CompanyNumber}</div>
                      </div>
                      {invoiceData.companyData.CompanyLogoSrc && (
                        <img 
                          src={invoiceData.companyData.CompanyLogoSrc}
                          alt="Company Logo" 
                          className="h-16 w-auto object-contain"
                        />
                      )}
                    </div>
                    
                    <div className="border-t border-b border-gray-200 py-4 my-4">
                      <div className="text-sm font-semibold text-gray-700">Invoice For:</div>
                      <div className="text-lg font-semibold mt-1">{invoiceData.projectName}</div>
                      {invoiceData.clientName && (
                        <div className="text-sm text-gray-600 mt-1">Client: {invoiceData.clientName}</div>
                      )}
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Project Price:</span>
                        <span className="font-semibold">£{invoiceData.projectPrice.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Labor Hours:</span>
                        <span className="font-semibold">{invoiceData.laborHours.toFixed(2)} hrs</span>
                      </div>
                      {invoiceData.materials.length > 0 && (
                        <>
                          <div className="border-t border-gray-200 pt-2 mt-2">
                            <div className="font-semibold text-gray-700 mb-2">Materials:</div>
                            {invoiceData.materials.map((mat, idx) => (
                              <div key={idx} className="flex justify-between ml-2">
                                <span className="text-gray-600">{mat.description || `Material ${idx + 1}`}:</span>
                                <span className="font-semibold">£{mat.price.toFixed(2)}</span>
                              </div>
                            ))}
                            <div className="flex justify-between font-semibold mt-1">
                              <span>Materials Total:</span>
                              <span>£{invoiceData.materials.reduce((sum, m) => sum + (m.price || 0), 0).toFixed(2)}</span>
                            </div>
                          </div>
                        </>
                      )}
                      {invoiceData.isVatDeductible && (
                        <div className="flex justify-between text-red-600">
                          <span>VAT Deductible (20%):</span>
                          <span className="font-semibold">-£{((invoiceData.projectPrice + invoiceData.materials.reduce((sum, m) => sum + (m.price || 0), 0)) * 0.20).toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-semibold">
                          £{(
                            invoiceData.isVatDeductible 
                              ? (invoiceData.projectPrice + invoiceData.materials.reduce((sum, m) => sum + (m.price || 0), 0)) * 0.80 
                              : (invoiceData.projectPrice + invoiceData.materials.reduce((sum, m) => sum + (m.price || 0), 0))
                          ).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">VAT ({invoiceData.vatRate}%):</span>
                        <span className="font-semibold">
                          £{(
                            (invoiceData.isVatDeductible 
                              ? (invoiceData.projectPrice + invoiceData.materials.reduce((sum, m) => sum + (m.price || 0), 0)) * 0.80 
                              : (invoiceData.projectPrice + invoiceData.materials.reduce((sum, m) => sum + (m.price || 0), 0))
                            ) * (invoiceData.vatRate / 100)
                          ).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2 mt-2">
                        <span>Total:</span>
                        <span>
                          £{(
                            (invoiceData.isVatDeductible 
                              ? (invoiceData.projectPrice + invoiceData.materials.reduce((sum, m) => sum + (m.price || 0), 0)) * 0.80 
                              : (invoiceData.projectPrice + invoiceData.materials.reduce((sum, m) => sum + (m.price || 0), 0))
                            ) * (1 + invoiceData.vatRate / 100)
                          ).toFixed(2)}
                        </span>
                      </div>
                      {invoiceData.customerVatNumber && (
                        <div className="text-xs text-gray-600 mt-2">
                          Customer VAT: {invoiceData.customerVatNumber}
                        </div>
                      )}
                      {invoiceData.paymentLink && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                          <div className="text-xs font-semibold text-blue-900">Payment Link:</div>
                          <a href={invoiceData.paymentLink} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline break-all">
                            {invoiceData.paymentLink}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => generateInvoice('print')}
                  disabled={saving}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
                >
                  {saving ? 'Generating...' : 'Print Only'}
                </button>
                <button
                  type="button"
                  onClick={sendInvoice}
                  disabled={invoiceSending || invoiceSent}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${invoiceSent ? 'bg-emerald-700' : 'bg-emerald-600 hover:bg-emerald-500'}`}
                >
                  {invoiceSent ? 'Sent' : invoiceSending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
        </main>
      </div>
    </div>
  );
}